import {
  centralOrganizationListSchema,
  formatDate,
  formatPercent,
  organizationDetailSchema,
  PROJECT_PHASE_LABELS,
  projectTemplateListSchema,
  projectTemplateSchema,
  type ProjectTemplateInput,
  type ProjectTemplateSummary,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, LayoutTemplate, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { templateCounts } from '@/features/templates/plural';
import { TemplateForm } from '@/features/templates/TemplateForm';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';

const templatesKey = ['project-templates'] as const;

/**
 * Modelos de projeto (T-204b, RF-50; DEC-070): a lista do que cada modelo traz, o conteúdo e, para o Admin Fluxus,
 * criar a partir do projeto de uma empresa ("Novo modelo" aqui ou "Salvar como modelo" na tela da empresa), renomear
 * e excluir.
 */
export function ModelosPage() {
  const { client } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const canManage = can('central-empresas', 'admin');
  const [renaming, setRenaming] = useState<ProjectTemplateSummary | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const [deleting, setDeleting] = useState<ProjectTemplateSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const list = useQuery({
    queryKey: templatesKey,
    queryFn: ({ signal }) =>
      client.request('/project-templates', { schema: projectTemplateListSchema, signal }),
  });

  async function rename(t: ProjectTemplateSummary, input: ProjectTemplateInput) {
    const saved = await client.request(`/project-templates/${t.id}`, {
      method: 'PATCH',
      body: input,
      schema: projectTemplateSchema,
    });
    setRenaming(null);
    await queryClient.invalidateQueries({ queryKey: templatesKey });
    toast.success(`Modelo ${saved.name} salvo`);
  }

  const remove = useMutation({
    meta: { handlesError: true },
    mutationFn: (t: ProjectTemplateSummary) =>
      client.request(`/project-templates/${t.id}`, { method: 'DELETE' }).then(() => t),
    onSuccess: async (t) => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: templatesKey });
      toast.success(`Modelo ${t.name} excluído`);
    },
    onError: (err) => toast.error('Não foi possível excluir', { description: errorMessage(err) }),
  });

  return (
    <PageContainer>
      <PageHeader
        title="Modelos de projeto"
        description="Estruturas de BSC da Fluxus — perspectivas, objetivos corporativos, relações e KPIs, sem metas, resultados, áreas ou pessoas — para abrir projetos novos a partir delas. Um modelo nasce de um projeto de uma empresa."
        actions={
          canManage ? (
            <Button onClick={() => setCreating(true)}>
              <Plus aria-hidden />
              Novo modelo
            </Button>
          ) : null
        }
      />
      <div className="mt-6">
        {list.isPending ? (
          <LoadingState rows={3} label="Carregando modelos…" />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
        ) : list.data.items.length === 0 ? (
          <EmptyState
            icon={LayoutTemplate}
            title="Nenhum modelo de projeto"
            description={
              canManage
                ? 'Um modelo guarda a estrutura de um projeto BSC para abrir projetos de outras empresas a partir dela. Crie o primeiro com “Novo modelo” ou com “Salvar como modelo” no projeto de uma empresa.'
                : 'Um modelo guarda a estrutura de um projeto BSC para abrir projetos de outras empresas a partir dela. O Admin Fluxus cria os modelos a partir dos projetos das empresas.'
            }
            action={
              <Button asChild variant="outline">
                <Link to="/empresas">Ir para a Central de Empresas</Link>
              </Button>
            }
          />
        ) : (
          <ul className="flex flex-col gap-3">
            {list.data.items.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                onRename={canManage ? () => setRenaming(t) : undefined}
                onDelete={canManage ? () => setDeleting(t) : undefined}
              />
            ))}
          </ul>
        )}
      </div>

      <Dialog
        open={creating}
        onOpenChange={setCreating}
        busy={createBusy}
        title="Novo modelo"
        description="Escolha a empresa e o projeto cuja estrutura vira o modelo."
      >
        {creating ? (
          <NewTemplate
            onCreated={async (t) => {
              setCreating(false);
              await queryClient.invalidateQueries({ queryKey: templatesKey });
              toast.success(`Modelo ${t.name} criado`, { description: templateCounts(t.counts) });
            }}
            onCancel={() => setCreating(false)}
            onBusyChange={setCreateBusy}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={renaming !== null}
        onOpenChange={(open) => !open && setRenaming(null)}
        busy={renameBusy}
        title="Renomear modelo"
      >
        {renaming ? (
          <TemplateForm
            initial={{ name: renaming.name, description: renaming.description }}
            submitLabel="Salvar"
            onSubmit={(input) => rename(renaming, input)}
            onCancel={() => setRenaming(null)}
            onBusyChange={setRenameBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Excluir o modelo?"
        description={
          deleting
            ? `${deleting.name} deixa de aparecer para abrir projetos. Projetos já abertos com ele não mudam.`
            : ''
        }
        confirmLabel="Excluir"
        destructive
        busy={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
      />
    </PageContainer>
  );
}

function TemplateCard({
  template: t,
  onRename,
  onDelete,
}: {
  template: ProjectTemplateSummary;
  onRename?: () => void;
  onDelete?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const contentId = `modelo-${t.id}`;
  return (
    <li>
      <article aria-label={t.name} className="rounded-lg border border-border bg-surface p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-bold">{t.name}</h2>
            {t.description ? <p className="mt-0.5 text-sm text-muted-foreground">{t.description}</p> : null}
            <p className="mt-2 text-sm">{templateCounts(t.counts)}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t.source
                ? `Criado a partir de ${t.source.project} (${t.source.organization})`
                : 'Criado a partir de um projeto de outra empresa'}{' '}
              · {t.createdByName ? `por ${t.createdByName} ` : ''}em {formatDate(t.createdAt)}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              aria-expanded={open}
              aria-controls={contentId}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
              {open ? 'Ocultar conteúdo' : 'Ver conteúdo'}
            </Button>
            {onRename ? (
              <Button variant="outline" size="sm" onClick={onRename} aria-label={`Renomear ${t.name}`}>
                <Pencil aria-hidden />
                Renomear
              </Button>
            ) : null}
            {onDelete ? (
              <Button variant="ghost" size="sm" onClick={onDelete} aria-label={`Excluir ${t.name}`}>
                <Trash2 aria-hidden />
                Excluir
              </Button>
            ) : null}
          </div>
        </div>
        {open ? (
          <div id={contentId} className="mt-4 border-t border-border pt-4">
            <TemplateContent templateId={t.id} />
          </div>
        ) : null}
      </article>
    </li>
  );
}

/** O que o modelo traz: perspectivas (com peso), os objetivos e KPIs de cada uma e as relações. */
function TemplateContent({ templateId }: { templateId: string }) {
  const { client } = useAuth();
  const detail = useQuery({
    queryKey: [...templatesKey, templateId],
    queryFn: ({ signal }) =>
      client.request(`/project-templates/${templateId}`, { schema: projectTemplateSchema, signal }),
  });
  if (detail.isPending) return <LoadingState rows={2} label="Carregando o conteúdo…" />;
  if (detail.isError) {
    return (
      <ErrorState
        className="py-6"
        error={detail.error}
        onRetry={() => void detail.refetch()}
        retrying={detail.isFetching}
      />
    );
  }
  const { perspectives, objectives, relations, kpis } = detail.data.content;
  const objectiveName = new Map(objectives.map((o) => [o.key, o.name]));
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 md:grid-cols-2">
        {[...perspectives]
          .sort((a, b) => a.position - b.position)
          .map((p) => {
            const own = objectives.filter((o) => o.perspectiveKey === p.key);
            const ownKpis = kpis.filter((k) => k.perspectiveKey === p.key);
            return (
              <section key={p.key} aria-label={`Perspectiva ${p.name}`}>
                <h3 className="text-sm font-bold">
                  {p.name}
                  {p.weight !== null ? (
                    <span className="font-normal text-muted-foreground">
                      {' '}
                      · peso {formatPercent(p.weight, 0)}
                    </span>
                  ) : null}
                </h3>
                {own.length || ownKpis.length ? (
                  <ul className="mt-1 flex flex-col gap-0.5 text-sm">
                    {own.map((o) => (
                      <li key={o.key}>
                        <span className="text-muted-foreground">Objetivo:</span> {o.name}
                        {o.weight !== null ? ` (${formatPercent(o.weight, 0)})` : ''}
                      </li>
                    ))}
                    {ownKpis.map((k) => (
                      <li key={k.code}>
                        <span className="text-muted-foreground">KPI:</span> {k.code} · {k.name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">Sem objetivos nem KPIs.</p>
                )}
              </section>
            );
          })}
      </div>
      {relations.length ? (
        <section aria-label="Relações de causa e efeito">
          <h3 className="text-sm font-bold">Relações</h3>
          <ul className="mt-1 flex flex-col gap-0.5 text-sm">
            {relations.map((r) => (
              <li key={`${r.fromKey}-${r.toKey}-${r.type}`}>
                {objectiveName.get(r.fromKey)} → {objectiveName.get(r.toKey)}
                {r.type === 'HORIZONTAL' ? ' (horizontal)' : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** Nova tentativa de uma lista que não carregou (a mensagem fica ligada ao campo). */
function RetryButton({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick} loading={busy}>
      {busy ? null : <RefreshCw aria-hidden />}
      Tentar novamente
    </Button>
  );
}

/** Novo modelo: a empresa (com busca), o projeto dela e o nome — o mesmo que "Salvar como modelo" no projeto. */
function NewTemplate({
  onCreated,
  onCancel,
  onBusyChange,
}: {
  onCreated: (t: ProjectTemplateSummary) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { client } = useAuth();
  // Enquanto o modelo é criado, empresa e projeto não mudam (o envio é do projeto escolhido).
  const [sending, setSending] = useState(false);
  const ids = { search: useId(), org: useId(), project: useId() };
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  // A empresa escolhida continua como opção mesmo quando uma nova busca não a traz (o que se vê é o que se envia).
  const [chosen, setChosen] = useState<{ id: string; name: string } | null>(null);
  const [projectId, setProjectId] = useState('');
  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => setQ(next), 300);
    return () => clearTimeout(timer);
  }, [search, q]);
  const orgs = useQuery({
    queryKey: ['organizations', 'template-source', q],
    queryFn: ({ signal }) =>
      client.request('/organizations', {
        query: { q: q || undefined, pageSize: 100 },
        schema: centralOrganizationListSchema,
        signal,
      }),
  });
  const detail = useQuery({
    // A mesma chave da tela da empresa: abrir ou arquivar projeto lá já atualiza a lista daqui.
    queryKey: ['organizations', 'detail', organizationId],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}`, { schema: organizationDetailSchema, signal }),
    enabled: Boolean(organizationId),
  });
  const project = detail.data?.projects.find((p) => p.id === projectId) ?? null;
  // Falhas de carregamento ligadas aos seletores (aria-invalid e aria-describedby).
  const orgError = orgs.isError ? errorMessage(orgs.error) : undefined;
  const projectError = detail.isError ? errorMessage(detail.error) : undefined;
  const noProjects = Boolean(detail.data && detail.data.projects.length === 0);

  async function create(input: ProjectTemplateInput) {
    const t = await client.request(`/organizations/${organizationId}/projects/${projectId}/template`, {
      method: 'POST',
      body: input,
      schema: projectTemplateSchema,
    });
    await onCreated(t);
  }

  return (
    <div className="flex flex-col gap-5">
      <FormField id={ids.search} label="Buscar empresa">
        <Input
          id={ids.search}
          type="search"
          value={search}
          disabled={sending}
          placeholder="Nome da empresa"
          onChange={(e) => setSearch(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField
        id={ids.org}
        label="Empresa"
        error={orgError}
        action={
          orgs.isError ? (
            <RetryButton onClick={() => void orgs.refetch()} busy={orgs.isFetching} />
          ) : undefined
        }
      >
        <NativeSelect
          {...fieldA11y(ids.org, orgError)}
          value={organizationId}
          disabled={orgs.isPending || orgs.isError || sending}
          onChange={(e) => {
            const id = e.target.value;
            const name = orgs.data?.items.find((o) => o.id === id)?.name;
            setOrganizationId(id);
            setChosen(id && name ? { id, name } : null);
            setProjectId('');
          }}
        >
          <option value="">
            {orgs.isPending
              ? 'Carregando empresas…'
              : orgs.data && orgs.data.items.length === 0
                ? 'Nenhuma empresa encontrada'
                : 'Escolha a empresa'}
          </option>
          {chosen && !orgs.data?.items.some((o) => o.id === chosen.id) ? (
            <option value={chosen.id}>{chosen.name}</option>
          ) : null}
          {orgs.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      {organizationId ? (
        <FormField
          id={ids.project}
          label="Projeto"
          error={projectError}
          action={
            detail.isError ? (
              <RetryButton onClick={() => void detail.refetch()} busy={detail.isFetching} />
            ) : undefined
          }
          hint={
            noProjects ? (
              <p className="text-sm text-muted-foreground">Esta empresa ainda não tem projeto BSC.</p>
            ) : undefined
          }
        >
          <NativeSelect
            {...fieldA11y(ids.project, projectError, noProjects)}
            value={projectId}
            disabled={detail.isPending || detail.isError || sending}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">{detail.isPending ? 'Carregando projetos…' : 'Escolha o projeto'}</option>
            {detail.data?.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {p.status === 'ACTIVE' ? PROJECT_PHASE_LABELS[p.phase] : 'arquivado'}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      ) : null}
      {project ? (
        <TemplateForm
          key={project.id}
          initial={{ name: project.name, description: null }}
          submitLabel="Criar modelo"
          onSubmit={create}
          onCancel={onCancel}
          onBusyChange={(busy) => {
            setSending(busy);
            onBusyChange(busy);
          }}
        />
      ) : (
        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      )}
    </div>
  );
}
